import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent,
} from "react";
import {
  Dialog as HuiDialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import clsx from "clsx";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { LockedAutocomplete } from "@/components/LockedAutocomplete";
import type { AutocompleteOption } from "@/components/LockedAutocomplete";
import { AiSuggestionsPanel } from "@/pages/material/AiSuggestionsPanel";
import { Lightbox } from "@/pages/material/Lightbox";
import { useMaMediaContextMenu } from "@/pages/material/useMaMediaContextMenu";
import { imageElementFromContextEvent } from "@/lib/clipboardMedia";
import { MaPositionMaterialField } from "@/pages/material/MaPositionMaterialField";
import { QrCodeIcon } from "@heroicons/react/24/outline";
import { MaArtikelScanDialog, type MaArtikelScanAdd } from "@/pages/material/MaArtikelScanDialog";
import {
  addMaAttachment,
  addMaPosition,
  analyzeImageWithAi,
  createLinkedAgaFromMa,
  createMaterialAnforderung,
  deleteMaAttachment,
  deleteMaPosition,
  deleteMaterialAnforderung,
  findAuftragByNumber,
  getMaAttachmentThumbUrl,
  getMaAttachmentUrl,
  getMaterialAnforderung,
  getRecentAuftraege,
  markMaterialAnforderungSeen,
  patchMaPosition,
  patchMaterialAnforderung,
  searchAuftraege,
} from "@/lib/material";
import { notifyMaterialBadgeRefresh } from "@/lib/navBadges";
import type {
  AiAnalysisResult,
  AuftragLookup,
  MaDetail,
  MaPosition,
  MaPositionDraftLink,
  MaPositionInput,
  MaPositionOrderedLink,
} from "@/lib/material";
import {
  getProjectContact,
  type AgaContactMode,
  type ProjectContact,
} from "@/lib/angebotsanfragen";
import { ApiError } from "@/lib/apiClient";
import { getUserLocalLogin, loginsMatch, peekCurrentUser } from "@/lib/auth";
import { toast } from "@/lib/toast";
import { formatAuftragNumberWithYear } from "@/lib/orderDraftUtils";
import {
  filterMaterialUnits,
  normalizeMaterialUnit,
} from "@/lib/materialUnits";
import { getOrder } from "@/lib/projectOverview";
import { ProjectOverviewButton } from "@/components/ProjectOverviewButton";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import {
  DialogCloseButton,
  DiscardChangesDialog,
  useDialogCloseGuard,
} from "@/components/ui/dialogCloseGuard";
import { useModalStackLayer } from "@/lib/modalStack";
import { isDialogDismissSuppressed } from "@/components/ui/useDialogDismissGuard";
import { MaBueroPanel } from "@/pages/material/MaBueroPanel";
import { MaMobileUploadDialog } from "@/pages/material/MaMobileUploadDialog";
import { MaLieferKwRangePicker } from "@/pages/material/MaLieferKwRangePicker";
import {
  formatMaPositionOrderSummary,
  maBearbeiterDisplayName,
  maFortschrittLabel,
  MaFortschrittBadge,
  MaPositionOrderBadge,
  maPositionOrderSummary,
  normalizeMonteurFortschritt,
} from "@/pages/material/maListDisplay";
import {
  currentIsoWeekYear,
  formatLieferKw,
  formatLieferKwFromFields,
  isFixterminWithinLieferKw,
  lieferKwDateBounds,
  parseLieferKwInput,
  type MaLieferKwRange,
} from "@/lib/maLieferKw";
import "@/pages/material/materialMobile.css";


export interface MaDialogProps {
  open: boolean;
  onClose: () => void;
  /** Wird aufgerufen, wenn die Liste neu geladen werden soll. */
  onChanged: () => void;
  /** ID einer bestehenden MA, oder null/undefined für „neu anlegen“. */
  maId: number | null;
  /** True = der angemeldete User ist Büro/Admin (Büro-Ansicht). */
  canProcess: boolean;
  /** Auftrag vorausfüllen, wenn eine neue MA geöffnet wird (QR-/Deep-Link). */
  initialAuftragId?: number | null;
  /** Tailwind z-index class for stacking over other modals. */
  zIndexClass?: string;
}

interface PositionDraft {
  /** Vorhandene MA-Position-ID, wenn schon gespeichert. */
  id: number | null;
  /** Lokale ID, um React-Keys stabil zu halten. */
  localId: string;
  menge: string;
  einheit: string;
  text: string;
  link_url: string;
  notes: string;
  /** Optional: Verknüpfung in den lokalen Hero-Artikelkatalog. */
  artikel_id: number | null;
  hero_nr: string | null;
  hero_unit: string | null;
  ean: string | null;
  /** Aus KI-Bildanalyse übernommen – Badge „Von KI erkannt“. */
  from_ai: boolean;
  bereitgelegt_at?: string | null;
  position_status?: "storniert" | "abgelehnt" | null;
  position_status_grund?: string | null;
  ordered_links?: MaPositionOrderedLink[];
  draft_links?: MaPositionDraftLink[];
}

/**
 * Anzeige-Status für die KI-Auftragserkennung.  Wird im Panel als Chip
 * angezeigt, damit der Monteur sofort sieht, ob ein Auftrag automatisch
 * verknüpft wurde oder ob er nachhelfen muss.
 */
export type AuftragHintStatus =
  | { kind: "idle" }
  | { kind: "looking"; value: string }
  | { kind: "linked"; value: string; auftrag: AuftragLookup }
  | { kind: "already-set"; value: string; current: string }
  | { kind: "not-found"; value: string }
  | { kind: "error"; value: string; message: string };

interface PendingAttachment {
  localId: string;
  file: File;
  /** Optional an eine Position binden (per localId). */
  positionLocalId?: string | null;
  previewUrl: string;
}

let LOCAL_ID_COUNTER = 0;
function nextLocalId(prefix: string): string {
  LOCAL_ID_COUNTER += 1;
  return `${prefix}-${LOCAL_ID_COUNTER}-${Math.random().toString(36).slice(2, 8)}`;
}

function makeEmptyPosition(): PositionDraft {
  return {
    id: null,
    localId: nextLocalId("p"),
    menge: "",
    einheit: "",
    text: "",
    link_url: "",
    notes: "",
    artikel_id: null,
    hero_nr: null,
    hero_unit: null,
    ean: null,
    from_ai: false,
  };
}

function positionFromDetail(p: MaPosition): PositionDraft {
  return {
    id: p.id,
    localId: nextLocalId("p"),
    menge: p.menge != null ? String(p.menge) : "",
    einheit: p.einheit ?? "",
    text: p.text ?? "",
    link_url: p.link_url ?? "",
    notes: p.notes ?? "",
    artikel_id: p.artikel_id ?? null,
    hero_nr: p.hero_nr ?? null,
    hero_unit: p.hero_unit ?? null,
    ean: p.ean ?? null,
    from_ai: Boolean(p.from_ai),
    bereitgelegt_at: p.bereitgelegt_at ?? null,
    position_status: p.position_status ?? null,
    position_status_grund: p.position_status_grund ?? null,
    ordered_links: p.ordered_links || [],
    draft_links: p.draft_links || [],
  };
}

function positionToInput(
  p: PositionDraft,
  sortIndex: number,
): MaPositionInput | null {
  const text = p.text.trim();
  if (!text) return null;
  const menge = parseFloat(p.menge.replace(",", "."));
  return {
    sort_index: sortIndex,
    menge: Number.isFinite(menge) ? menge : null,
    einheit: p.einheit.trim() || null,
    text,
    link_url: p.link_url.trim() || null,
    notes: p.notes.trim() || null,
    artikel_id: p.artikel_id,
    hero_nr: p.hero_nr,
    hero_unit: p.hero_unit,
    ean: p.ean,
    from_ai: p.from_ai,
  };
}

function fmtDate(value: string | null | undefined): string {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Lokales Kalenderdatum als yyyy-mm-dd (für type=date min/max). */
function localTodayIso(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function isLieferterminBeforeToday(iso: string): boolean {
  return iso < localTodayIso();
}

function resolveLieferKwPayload(raw: string): {
  ok: true;
  range: MaLieferKwRange | null;
  fields: {
    liefer_kw_von: number | null;
    liefer_kw_bis: number | null;
    liefer_kw_jahr: number | null;
  };
} | { ok: false; error: string } {
  const text = raw.trim();
  if (!text) {
    return {
      ok: true,
      range: null,
      fields: {
        liefer_kw_von: null,
        liefer_kw_bis: null,
        liefer_kw_jahr: null,
      },
    };
  }
  const parsed = parseLieferKwInput(text, currentIsoWeekYear());
  if (!parsed.ok) return parsed;
  return {
    ok: true,
    range: parsed.range,
    fields: {
      liefer_kw_von: parsed.range.von,
      liefer_kw_bis: parsed.range.bis,
      liefer_kw_jahr: parsed.range.jahr,
    },
  };
}

function isMaAttachmentFile(file: File): boolean {
  const type = file.type.toLowerCase();
  if (type.startsWith("image/") || type === "application/pdf") return true;
  const name = file.name.toLowerCase();
  return (
    name.endsWith(".pdf") ||
    /\.(png|jpe?g|gif|webp|heic|heif|bmp|tif|tiff)$/.test(name)
  );
}

export function MaDialog({
  open,
  onClose,
  onChanged,
  maId,
  canProcess,
  initialAuftragId = null,
  zIndexClass = "z-50",
}: MaDialogProps) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState<MaDetail | null>(null);

  const [notes, setNotes] = useState("");
  const [auftrag, setAuftrag] =
    useState<AutocompleteOption<AuftragLookup> | null>(null);
  const [auftragError, setAuftragError] = useState<string | null>(null);
  const auftragRef = useRef<AutocompleteOption<AuftragLookup> | null>(null);
  useEffect(() => {
    auftragRef.current = auftrag;
  }, [auftrag]);
  const [lieferKwText, setLieferKwText] = useState("");
  const [lieferKwError, setLieferKwError] = useState<string | null>(null);
  const [liefertermin, setLiefertermin] = useState("");
  const [lieferterminFix, setLieferterminFix] = useState(false);
  const [lieferterminError, setLieferterminError] = useState<string | null>(null);
  const [alsoOfferAsAga, setAlsoOfferAsAga] = useState(false);
  const [agaContactMode, setAgaContactMode] = useState<AgaContactMode>("project");
  const [agaProjectContact, setAgaProjectContact] = useState<ProjectContact | null>(null);
  const [agaContactName, setAgaContactName] = useState("");
  const [agaContactCompany, setAgaContactCompany] = useState("");
  const [agaContactPhone, setAgaContactPhone] = useState("");
  const [agaContactEmail, setAgaContactEmail] = useState("");
  const [agaContactNote, setAgaContactNote] = useState("");
  const [positions, setPositions] = useState<PositionDraft[]>([]);
  const [pending, setPending] = useState<PendingAttachment[]>([]);
  // Aktueller, noch nicht übernommener Freitext je Position (localId → Text).
  // Wird synchron (per Ref) gehalten, damit ein beim Tippen offenes Material-
  // Feld beim Klick auf „Speichern“ nicht verloren geht (Blur/Enter committen
  // sonst zu spät, siehe MaPositionMaterialField/LockedAutocomplete).
  const pendingPosTextRef = useRef<Map<string, string>>(new Map());
  const setPendingPosText = useCallback((localId: string, text: string) => {
    const map = pendingPosTextRef.current;
    if (text.trim()) map.set(localId, text.trim());
    else map.delete(localId);
  }, []);

  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState<AiAnalysisResult | null>(null);
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiFileName, setAiFileName] = useState<string | null>(null);
  const [auftragHintStatus, setAuftragHintStatus] =
    useState<AuftragHintStatus>({ kind: "idle" });

  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [lightboxCopySource, setLightboxCopySource] = useState<{
    url: string;
    filename: string;
    contentType: string;
  } | null>(null);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const fileDropCounterRef = useRef(0);
  const [fileDropActive, setFileDropActive] = useState(false);
  const [mobileUploadOpen, setMobileUploadOpen] = useState(false);
  const [artikelScanOpen, setArtikelScanOpen] = useState(false);
  const [mobileUploadPreparing, setMobileUploadPreparing] = useState(false);
  const mobileUploadSavingRef = useRef(false);
  const createdMaIdRef = useRef<number | null>(null);

  const isMobile = useMediaQuery("(max-width: 767px)");
  const parsedLieferKw = useMemo(() => {
    const text = lieferKwText.trim();
    if (!text) return null;
    const parsed = parseLieferKwInput(text, currentIsoWeekYear());
    return parsed.ok ? parsed.range : null;
  }, [lieferKwText]);
  const lieferterminBounds = useMemo(() => {
    const today = localTodayIso();
    if (!parsedLieferKw) {
      return { min: today, max: undefined as string | undefined };
    }
    const { minIso, maxIso } = lieferKwDateBounds(parsedLieferKw);
    return {
      min: minIso > today ? minIso : today,
      max: maxIso,
    };
  }, [parsedLieferKw, open]);

  const applyLieferKwText = useCallback((next: string) => {
    setLieferKwText(next);
    if (!next.trim()) {
      setLieferKwError(null);
      return;
    }
    const parsed = parseLieferKwInput(next, currentIsoWeekYear());
    if (!parsed.ok) {
      setLieferKwError(parsed.error);
      return;
    }
    setLieferKwError(null);
    setLiefertermin((prev) => {
      if (!prev) return prev;
      if (isFixterminWithinLieferKw(prev, parsed.range)) {
        setLieferterminError(null);
        return prev;
      }
      setLieferterminFix(false);
      setLieferterminError(
        "Fixtermin lag außerhalb der neuen Kalenderwochen und wurde zurückgesetzt.",
      );
      toast.info("Fixtermin außerhalb der KW – zurückgesetzt.");
      return "";
    });
  }, []);

  const isNew = !maId;
  const agaProjectHasContact = !!agaProjectContact?.ansprechpartner_id;
  const status = detail?.status;
  const showBueroView = canProcess && detail && !isNew;
  const canEdit = useMemo(() => {
    if (showBueroView) return false;
    if (isNew) return true;
    if (status === "erledigt" || status === "abgelehnt") return false;
    if (detail?.bearbeitet_von) return false;
    if (!canProcess && detail?.created_by) {
      const session = peekCurrentUser();
      const candidates = [
        getUserLocalLogin(session),
        String(session?.samAccountName || ""),
        String(session?.username || ""),
        String(session?.sub || ""),
      ].filter(Boolean);
      if (
        candidates.length > 0 &&
        !candidates.some((candidate) => loginsMatch(detail.created_by, candidate))
      ) {
        return false;
      }
    }
    return true;
  }, [showBueroView, isNew, status, detail?.bearbeitet_von, detail?.created_by, canProcess]);

  /* ─────────────────────────── Daten laden ─────────────────────────── */
  const reload = useCallback(
    async (id: number | null, options?: { silent?: boolean }) => {
      const silent = options?.silent ?? false;
      // Positionen werden gleich neu gesetzt – offene Freitext-Puffer verwerfen,
      // damit keine veralteten localId-Einträge zurückbleiben.
      pendingPosTextRef.current.clear();
      if (id == null) {
        setDetail(null);
        setNotes("");
        setAuftrag(null);
        setAuftragError(null);
        setLieferKwText("");
        setLieferKwError(null);
        setLiefertermin("");
        setLieferterminFix(false);
        setLieferterminError(null);
        setAlsoOfferAsAga(false);
        setAgaContactMode("project");
        setAgaProjectContact(null);
        setAgaContactName("");
        setAgaContactCompany("");
        setAgaContactPhone("");
        setAgaContactEmail("");
        setAgaContactNote("");
        setPositions([]);
        setPending([]);
        setAiResult(null);
        setAiError(null);
        setAiFileName(null);
        setAuftragHintStatus({ kind: "idle" });
        return;
      }
      if (!silent) setLoading(true);
      try {
        const ma = await getMaterialAnforderung(id);
        setDetail(ma);
        setNotes(ma.notes || "");
        setLieferKwText(
          formatLieferKwFromFields(
            ma.liefer_kw_von,
            ma.liefer_kw_bis,
            ma.liefer_kw_jahr,
            { asciiHyphen: true },
          ),
        );
        setLieferKwError(null);
        setLiefertermin(ma.liefertermin ?? "");
        setLieferterminFix(!!ma.liefertermin_fix);
        setLieferterminError(null);
        setAuftragError(null);
        setAuftrag(
          ma.auftrag_id && ma.auftrag_nummer
            ? {
                key: String(ma.auftrag_id),
                label: ma.auftrag_nummer,
                value: {
                  id: ma.auftrag_id,
                  auftragsnummer: ma.auftrag_nummer,
                  beschreibung: null,
                  ort: null,
                  ort_zusatz: null,
                  kunde: null,
                },
              }
            : null,
        );
        setPositions((ma.positionen || []).map(positionFromDetail));
      } catch (err) {
        const message =
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : "Konnte Materialanforderung nicht laden.";
        toast.error(message);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [],
  );

  // Beim Öffnen / ID-Wechsel laden.
  useEffect(() => {
    if (!open) {
      createdMaIdRef.current = null;
      fileDropCounterRef.current = 0;
      setFileDropActive(false);
      return;
    }
    createdMaIdRef.current = maId ?? null;
    let cancelled = false;
    void (async () => {
      await reload(maId ?? null);
      if (cancelled || maId != null || !initialAuftragId) return;
      try {
        const order = await getOrder(initialAuftragId);
        if (cancelled) return;
        setAuftrag({
          key: String(order.id),
          label: formatAuftragNumberWithYear(order),
          value: {
            id: order.id,
            auftragsnummer: order.auftragsnummer,
            jahr: order.jahr ?? null,
            beschreibung: order.beschreibung ?? null,
            ort: order.ort ?? null,
            ort_zusatz: order.ort_zusatz ?? null,
            kunde: order.kunde ?? null,
          },
        });
        setAuftragError(null);
      } catch (err) {
        if (cancelled) return;
        setAuftragError(
          err instanceof ApiError
            ? err.message
            : "Auftrag aus dem Link konnte nicht geladen werden.",
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, maId, initialAuftragId, reload]);

  // Gelieferte/bereitgelegte MA angesehen → grünes Nav-Badge ausblenden (nur Monteur-Sicht).
  useEffect(() => {
    if (!open || !detail?.id || canProcess) return;
    if (
      detail.monteur_fortschritt !== "geliefert" &&
      detail.monteur_fortschritt !== "bereitgelegt"
    ) {
      return;
    }
    let cancelled = false;
    void markMaterialAnforderungSeen(detail.id)
      .then((res) => {
        if (cancelled || !res?.seen) return;
        notifyMaterialBadgeRefresh();
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [open, detail?.id, detail?.monteur_fortschritt, canProcess]);

  // Projekt-Ansprechpartner für parallele AGA laden.
  const selectedAuftragId = auftrag?.value?.id ?? null;
  useEffect(() => {
    if (!open || !isNew || !alsoOfferAsAga || !selectedAuftragId) {
      if (!selectedAuftragId) setAgaProjectContact(null);
      return;
    }
    let cancelled = false;
    void getProjectContact(selectedAuftragId)
      .then((contact) => {
        if (cancelled) return;
        setAgaProjectContact(contact);
        if (!contact.ansprechpartner_id && agaContactMode === "project") {
          setAgaContactMode("new");
        }
      })
      .catch(() => {
        if (!cancelled) setAgaProjectContact(null);
      });
    return () => {
      cancelled = true;
    };
    // agaContactMode bewusst nicht als Dependency – nur bei Projektwechsel laden.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isNew, alsoOfferAsAga, selectedAuftragId]);

  // Hintergrund nicht mitscrollen (auch horizontal).
  useEffect(() => {
    if (!open) return;
    const html = document.documentElement;
    const body = document.body;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    const prevHtmlOverflowX = html.style.overflowX;
    const prevBodyOverflowX = body.style.overflowX;
    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    html.style.overflowX = "hidden";
    body.style.overflowX = "hidden";
    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
      html.style.overflowX = prevHtmlOverflowX;
      body.style.overflowX = prevBodyOverflowX;
    };
  }, [open]);

  // Beim Schließen Pending-URLs aufräumen.
  useEffect(() => {
    if (open) return;
    pending.forEach((p) => {
      try {
        URL.revokeObjectURL(p.previewUrl);
      } catch {
        /* ignore */
      }
    });
    if (pending.length) setPending([]);
    setAiResult(null);
    setAiError(null);
    setAiFileName(null);
    setAuftragHintStatus({ kind: "idle" });
    setArtikelScanOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /* ─────────────────────────── Position handlers ─────────────────────────── */
  function addEmptyPosition(focusText = true): PositionDraft {
    const p = makeEmptyPosition();
    setPositions((prev) => [...prev, p]);
    if (focusText) {
      window.setTimeout(() => {
        const el = document.querySelector<HTMLInputElement>(
          `[data-pos-local="${p.localId}"] [data-field="text"]`,
        );
        el?.focus();
      }, 30);
    }
    return p;
  }

  function addScannedArtikel(item: MaArtikelScanAdd) {
    const p: PositionDraft = {
      ...makeEmptyPosition(),
      menge: item.menge,
      einheit: item.einheit,
      text: item.text,
      artikel_id: item.artikel_id,
      hero_nr: item.hero_nr,
      hero_unit: item.hero_unit,
      ean: item.ean,
    };
    setPositions((prev) => [...prev, p]);
    toast.success(
      `${item.text} · ${item.menge}${item.einheit ? ` ${item.einheit}` : ""} hinzugefügt.`,
    );
  }

  function patchPosition(localId: string, patch: Partial<PositionDraft>) {
    setPositions((prev) =>
      prev.map((p) => (p.localId === localId ? { ...p, ...patch } : p)),
    );
  }

  function removePosition(localId: string) {
    pendingPosTextRef.current.delete(localId);
    setPositions((prev) => prev.filter((p) => p.localId !== localId));
    // Auch ggf. zugehörige Pending-Anhänge entkoppeln (werden global angehängt).
    setPending((prev) =>
      prev.map((a) =>
        a.positionLocalId === localId ? { ...a, positionLocalId: null } : a,
      ),
    );
  }

  async function ensureSavedForMobileUpload(): Promise<number | null> {
    if (mobileUploadSavingRef.current) return null;

    const existingId = detail?.id ?? maId ?? createdMaIdRef.current ?? null;
    if (existingId) return existingId;

    setAuftragError(null);
    if (!auftrag) {
      setAuftragError(
        "Bitte einen Auftrag auswählen – ohne Auftragsnummer kann das Büro die Anforderung nicht zuordnen.",
      );
      toast.error(
        "Bitte wähle zuerst einen Auftrag aus, bevor du vom Handy hochladen kannst.",
      );
      return null;
    }

    const mergedPositions = positions.map((p) => {
      if (p.text.trim()) return p;
      const pendingText = pendingPosTextRef.current.get(p.localId);
      return pendingText && pendingText.trim()
        ? { ...p, text: pendingText.trim() }
        : p;
    });
    const positionInputs = mergedPositions
      .map((p, idx) => positionToInput(p, idx))
      .filter((p): p is MaPositionInput => p !== null);

    const kwResolved = resolveLieferKwPayload(lieferKwText);
    if (!kwResolved.ok) {
      setLieferKwError(kwResolved.error);
      toast.error(kwResolved.error);
      return null;
    }
    setLieferKwError(null);

    const lt = liefertermin.trim() ? liefertermin : null;
    if (
      lt &&
      isLieferterminBeforeToday(lt) &&
      lt !== (detail?.liefertermin ?? "")
    ) {
      setLieferterminError("Liefertermin muss heute oder in der Zukunft liegen.");
      toast.error("Bitte einen gültigen Liefertermin wählen.");
      return null;
    }
    if (lt && kwResolved.range && !isFixterminWithinLieferKw(lt, kwResolved.range)) {
      const msg =
        "Fixtermin muss innerhalb der gewählten Kalenderwochen liegen (spätestens in der letzten KW).";
      setLieferterminError(msg);
      toast.error(msg);
      return null;
    }
    setLieferterminError(null);

    mobileUploadSavingRef.current = true;
    setMobileUploadPreparing(true);
    try {
      const created = await createMaterialAnforderung({
        title: null,
        notes: notes.trim() || null,
        auftrag_id: auftrag.value.id,
        positionen: positionInputs,
        liefertermin: lt,
        liefertermin_fix: lieferterminFix,
        ...kwResolved.fields,
      });
      for (const att of pending) {
        await addMaAttachment(created.id, att.file, null);
      }
      pending.forEach((p) => {
        try {
          URL.revokeObjectURL(p.previewUrl);
        } catch {
          /* ignore */
        }
      });
      setPending([]);
      createdMaIdRef.current = created.id;
      await reload(created.id, { silent: true });
      onChanged();
      return created.id;
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Speichern fehlgeschlagen.";
      toast.error(message);
      return null;
    } finally {
      mobileUploadSavingRef.current = false;
      setMobileUploadPreparing(false);
    }
  }

  async function openMobileUpload() {
    if (mobileUploadPreparing) return;
    const workingMaId = await ensureSavedForMobileUpload();
    if (!workingMaId) return;
    setMobileUploadOpen(true);
  }

  /* ─────────────────────────── Anhänge ─────────────────────────── */
  function dragHasFiles(event: DragEvent): boolean {
    return Array.from(event.dataTransfer?.types || []).includes("Files");
  }

  function onAttachmentDragEnter(event: DragEvent) {
    if (!canEdit || !dragHasFiles(event)) return;
    event.preventDefault();
    fileDropCounterRef.current += 1;
    setFileDropActive(true);
  }

  function onAttachmentDragOver(event: DragEvent) {
    if (!canEdit || !dragHasFiles(event)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
  }

  function onAttachmentDragLeave(event: DragEvent) {
    if (!canEdit || !dragHasFiles(event)) return;
    event.preventDefault();
    fileDropCounterRef.current = Math.max(0, fileDropCounterRef.current - 1);
    if (fileDropCounterRef.current === 0) setFileDropActive(false);
  }

  function onAttachmentDrop(event: DragEvent) {
    if (!canEdit || !dragHasFiles(event)) return;
    event.preventDefault();
    fileDropCounterRef.current = 0;
    setFileDropActive(false);
    const dropped = Array.from(event.dataTransfer?.files || []);
    if (!dropped.length) return;
    const accepted = dropped.filter(isMaAttachmentFile);
    const rejected = dropped.length - accepted.length;
    if (rejected > 0) {
      toast.error(
        rejected === dropped.length
          ? "Diese Datei kann nicht angehängt werden. Erlaubt sind Bilder und PDF."
          : "Einige Dateien wurden übersprungen. Erlaubt sind Bilder und PDF.",
      );
    }
    if (accepted.length) void handleFiles(accepted);
  }

  async function handleFiles(files: FileList | File[] | null) {
    if (!files || files.length === 0) return;
    const arr = Array.from(files);
    for (const file of arr) {
      if (detail?.id) {
        try {
          await addMaAttachment(detail.id, file, null);
        } catch (err) {
          const message =
            err instanceof ApiError
              ? err.message
              : err instanceof Error
                ? err.message
                : "Anhang konnte nicht gespeichert werden.";
          toast.error(message);
        }
      } else {
        setPending((prev) => [
          ...prev,
          {
            localId: nextLocalId("att"),
            file,
            positionLocalId: null,
            previewUrl: URL.createObjectURL(file),
          },
        ]);
      }
      // KI-Analyse parallel anstoßen (nur Bilder, keine PDFs).
      if (file.type.startsWith("image/") && file.type !== "image/heic" && file.type !== "image/heif") {
        void runAiAnalysis(file);
      }
    }
    if (detail?.id) {
      void reload(detail.id);
    }
  }

  async function removeStoredAttachment(attachmentId: number) {
    if (!detail?.id) return;
    if (!confirm("Anhang wirklich entfernen?")) return;
    try {
      await deleteMaAttachment(detail.id, attachmentId);
      void reload(detail.id);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Anhang konnte nicht entfernt werden.";
      toast.error(message);
    }
  }

  function removePendingAttachment(localId: string) {
    setPending((prev) => {
      const found = prev.find((p) => p.localId === localId);
      if (found) {
        try {
          URL.revokeObjectURL(found.previewUrl);
        } catch {
          /* ignore */
        }
      }
      return prev.filter((p) => p.localId !== localId);
    });
  }

  /* ─────────────────────────── KI-Analyse ─────────────────────────── */
  async function runAiAnalysis(file: File) {
    setAiResult(null);
    setAiError(null);
    setAiFileName(file.name);
    setAuftragHintStatus({ kind: "idle" });
    setAiLoading(true);
    try {
      const res = await analyzeImageWithAi(file);
      setAiResult(res);
      // Auftrag aus dem KI-Hinweis automatisch verknüpfen.
      if (res.auftrag_hint?.value) {
        void resolveAuftragHint(res.auftrag_hint.value);
      }
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "KI-Analyse konnte nicht ausgeführt werden.";
      setAiError(message);
    } finally {
      setAiLoading(false);
    }
  }

  /**
   * Sucht zur erkannten Auftragsnummer den passenden Auftrag und legt ihn
   * automatisch als Verknüpfung an, sofern noch kein Auftrag gewählt ist.
   * Setzt zugleich den Status, der im Panel als Chip angezeigt wird.
   */
  async function resolveAuftragHint(rawValue: string) {
    const value = rawValue.trim().toUpperCase();
    if (!value) return;
    setAuftragHintStatus({ kind: "looking", value });
    try {
      const found = await findAuftragByNumber(value);
      if (!found) {
        setAuftragHintStatus({ kind: "not-found", value });
        return;
      }
      const current = auftragRef.current;
      if (current && current.value.id !== found.id) {
        // Nutzer hat schon manuell einen anderen Auftrag gewählt → nicht
        // überschreiben, nur Hinweis anzeigen.
        setAuftragHintStatus({
          kind: "already-set",
          value,
          current: current.label || current.value.auftragsnummer,
        });
        return;
      }
      const option: AutocompleteOption<AuftragLookup> = {
        key: String(found.id),
        label: formatAuftragNumberWithYear(found),
        description: [
          found.kunde,
          found.ort_zusatz || found.ort,
          found.beschreibung,
        ]
          .filter((v): v is string => !!v)
          .join(" · "),
        value: found,
      };
      setAuftrag(option);
      auftragRef.current = option;
      setAuftragError(null);
      setAuftragHintStatus({ kind: "linked", value, auftrag: found });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Auftrag konnte nicht nachgeschlagen werden.";
      setAuftragHintStatus({ kind: "error", value, message });
    }
  }

  function applyAiSuggestions(newPositions: MaPositionInput[]) {
    if (!newPositions.length) {
      setAiResult(null);
      setAiError(null);
      setAiFileName(null);
      setAuftragHintStatus({ kind: "idle" });
      return;
    }
    setPositions((prev) => {
      const next = prev.slice();
      for (const np of newPositions) {
        next.push({
          id: null,
          localId: nextLocalId("p"),
          menge: np.menge != null ? String(np.menge) : "",
          einheit: np.einheit ?? "",
          text: np.text,
          link_url: np.link_url ?? "",
          notes: np.notes ?? "",
          artikel_id: np.artikel_id ?? null,
          hero_nr: np.hero_nr ?? null,
          hero_unit: np.hero_unit ?? null,
          ean: np.ean ?? null,
          from_ai: true,
        });
      }
      return next;
    });
    setAiResult(null);
    setAiError(null);
    setAiFileName(null);
    setAuftragHintStatus({ kind: "idle" });
  }

  function dismissAi() {
    setAiResult(null);
    setAiError(null);
    setAiFileName(null);
    setAuftragHintStatus({ kind: "idle" });
  }

  /* ─────────────────────────── Speichern ─────────────────────────── */
  async function handleSave() {
    setAuftragError(null);

    // Pflichtfeld: Auftragsnummer.
    if (!auftrag) {
      setAuftragError(
        "Bitte einen Auftrag auswählen – ohne Auftragsnummer kann das Büro die Anforderung nicht zuordnen.",
      );
      toast.error(
        "Bitte wähle einen Auftrag aus, bevor du die Materialanforderung speicherst.",
      );
      return;
    }

    // Noch nicht übernommenen Freitext aus offenen Material-Feldern einmischen,
    // bevor Positionen ohne Text herausgefiltert werden – sonst geht die zuletzt
    // getippte Position beim direkten Klick auf „Speichern“ verloren.
    const mergedPositions = positions.map((p) => {
      if (p.text.trim()) return p;
      const pendingText = pendingPosTextRef.current.get(p.localId);
      return pendingText && pendingText.trim()
        ? { ...p, text: pendingText.trim() }
        : p;
    });

    const positionInputs = mergedPositions
      .map((p, idx) => positionToInput(p, idx))
      .filter((p): p is MaPositionInput => p !== null);

    const kwResolved = resolveLieferKwPayload(lieferKwText);
    if (!kwResolved.ok) {
      setLieferKwError(kwResolved.error);
      toast.error(kwResolved.error);
      return;
    }
    setLieferKwError(null);

    const lt = liefertermin.trim() ? liefertermin : null;
    if (
      lt &&
      isLieferterminBeforeToday(lt) &&
      lt !== (detail?.liefertermin ?? "")
    ) {
      setLieferterminError("Liefertermin muss heute oder in der Zukunft liegen.");
      toast.error("Bitte einen gültigen Liefertermin wählen.");
      return;
    }
    if (lt && kwResolved.range && !isFixterminWithinLieferKw(lt, kwResolved.range)) {
      const msg =
        "Fixtermin muss innerhalb der gewählten Kalenderwochen liegen (spätestens in der letzten KW).";
      setLieferterminError(msg);
      toast.error(msg);
      return;
    }
    setLieferterminError(null);

    if (isNew && alsoOfferAsAga) {
      if (agaContactMode === "project") {
        if (!agaProjectContact?.ansprechpartner_id) {
          toast.error(
            "Das Projekt hat keinen Ansprechpartner – bitte einen neuen anlegen.",
          );
          return;
        }
      } else if (!agaContactName.trim()) {
        toast.error("Bitte den Namen des Ansprechpartners angeben.");
        return;
      }
    }

    setSaving(true);
    try {
      let workingMaId = detail?.id ?? maId ?? null;
      let createdMaNummer: string | null = null;
      const creatingNew = !workingMaId;
      if (!workingMaId) {
        const created = await createMaterialAnforderung({
          title: null,
          notes: notes.trim() || null,
          auftrag_id: auftrag.value.id,
          positionen: positionInputs,
          liefertermin: lt,
          liefertermin_fix: lieferterminFix,
          ...kwResolved.fields,
        });
        workingMaId = created.id;
        createdMaNummer = created.ma_nummer;
      } else {
        await patchMaterialAnforderung(workingMaId, {
          notes: notes.trim() || null,
          auftrag_id: auftrag.value.id,
          liefertermin: lt,
          liefertermin_fix: lieferterminFix,
          ...kwResolved.fields,
        });
        await reconcilePositions(workingMaId, mergedPositions);
      }

      for (const att of pending) {
        await addMaAttachment(workingMaId, att.file, null);
      }
      pending.forEach((p) => {
        try {
          URL.revokeObjectURL(p.previewUrl);
        } catch {
          /* ignore */
        }
      });
      setPending([]);

      let agaNummer: string | null = null;
      if (creatingNew && alsoOfferAsAga) {
        try {
          const aga = await createLinkedAgaFromMa(workingMaId, {
            contact_mode: agaContactMode,
            ansprechpartner_id:
              agaContactMode === "project"
                ? agaProjectContact?.ansprechpartner_id ?? null
                : null,
            contact_name:
              agaContactMode === "new" ? agaContactName.trim() || null : null,
            contact_company:
              agaContactMode === "new" ? agaContactCompany.trim() || null : null,
            contact_phone:
              agaContactMode === "new" ? agaContactPhone.trim() || null : null,
            contact_email:
              agaContactMode === "new" ? agaContactEmail.trim() || null : null,
            contact_note: agaContactNote.trim() || null,
          });
          agaNummer = aga.aga_nummer;
        } catch (agaErr) {
          const agaMessage =
            agaErr instanceof ApiError
              ? agaErr.message
              : agaErr instanceof Error
                ? agaErr.message
                : "Angebotsanfrage konnte nicht angelegt werden.";
          toast.error(
            `Materialanforderung gespeichert, aber Angebotsanfrage fehlgeschlagen: ${agaMessage}`,
          );
          onChanged();
          onClose();
          return;
        }
      }

      if (agaNummer) {
        toast.success(
          `Materialanforderung${createdMaNummer ? ` ${createdMaNummer}` : ""} und Angebotsanfrage ${agaNummer} angelegt.`,
        );
      } else {
        toast.success("Materialanforderung gespeichert.");
      }
      onChanged();
      onClose();
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Speichern fehlgeschlagen.";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  }

  async function reconcilePositions(maId: number, drafts: PositionDraft[]) {
    const inputs = drafts
      .map((p, idx) => ({ p, idx, input: positionToInput(p, idx) }))
      .filter((row) => row.input !== null);

    // Update / Create
    for (const row of inputs) {
      const input = row.input as MaPositionInput;
      if (row.p.id) {
        await patchMaPosition(maId, row.p.id, input);
      } else {
        const res = await addMaPosition(maId, input);
        // Neue ID übernehmen, damit Reload nichts verschluckt
        row.p.id = res?.id ?? null;
      }
    }

    // Lösche entfernte Positionen
    const keepIds = new Set(
      inputs.filter((r) => r.p.id != null).map((r) => r.p.id as number),
    );
    const original = detail?.positionen || [];
    for (const orig of original) {
      if (!keepIds.has(orig.id)) {
        await deleteMaPosition(maId, orig.id);
      }
    }
  }

  /* ─────────────────────────── Status / Löschen ─────────────────────────── */
  async function handleDelete() {
    if (!detail) return;
    if (!confirm(`Materialanforderung „${detail.ma_nummer}“ wirklich löschen?`))
      return;
    try {
      await deleteMaterialAnforderung(detail.id);
      toast.success("Materialanforderung gelöscht.");
      onChanged();
      onClose();
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Löschen fehlgeschlagen.";
      toast.error(message);
    }
  }

  /* ─────────────────────────── Render ─────────────────────────── */
  const hasUnsaved = !detail || pending.length > 0;
  const { requestClose, confirmOpen, confirmDiscard, cancelDiscard } = useDialogCloseGuard(
    onClose,
    hasUnsaved,
  );
  const { zIndex: stackZIndex, bringToFront } = useModalStackLayer(open);

  const overlayZIndexClass =
    zIndexClass === "z-50" ? "z-[60]" : "z-[330]";

  return (
    <>
    <Transition show={open} as={Fragment}>
      <HuiDialog
        as="div"
        className={clsx("relative", zIndexClass)}
        style={stackZIndex != null ? { zIndex: stackZIndex } : undefined}
        onClose={() => {
          if (isDialogDismissSuppressed()) return;
          requestClose();
        }}
        onPointerDownCapture={bringToFront}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-150"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" aria-hidden />
        </TransitionChild>

        <div className="fixed inset-0 max-sm:overflow-hidden sm:overflow-y-auto">
          <div className="flex min-h-full items-start justify-center p-4 sm:items-center max-sm:h-full max-sm:min-h-0 max-sm:items-stretch max-sm:p-0 max-sm:overflow-x-hidden">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-2 scale-[0.99]"
              enterTo="opacity-100 translate-y-0 scale-100"
              leave="ease-in duration-100"
              leaveFrom="opacity-100 translate-y-0 scale-100"
              leaveTo="opacity-0 translate-y-2 scale-[0.99]"
            >
              <DialogPanel
                className={clsx(
                  "material-mobile-shell ma-dialog-panel relative flex w-full min-w-0 max-w-6xl transform flex-col overflow-hidden rounded-ios-lg bg-card shadow-ios-md transition",
                  "sm:max-h-[90vh]",
                  "max-sm:h-[100dvh] max-sm:max-h-[100dvh] max-sm:w-full max-sm:max-w-[100vw] max-sm:rounded-none max-sm:shadow-none",
                )}
                onDragEnter={onAttachmentDragEnter}
                onDragOver={onAttachmentDragOver}
                onDragLeave={onAttachmentDragLeave}
                onDrop={onAttachmentDrop}
              >
                {fileDropActive ? (
                  <div className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center border-2 border-dashed border-accent bg-accent/15">
                    <p className="rounded-ios bg-card px-4 py-2 text-sm font-medium text-label shadow-ios">
                      Bilder oder PDF hier ablegen
                    </p>
                  </div>
                ) : null}
                <header
                  className="ma-dialog-header flex shrink-0 items-center justify-between gap-2 border-b border-separator px-5 py-4 max-sm:px-3 max-sm:py-2 max-sm:pt-[env(safe-area-inset-top,0px)]"
                >
                  <div className="min-w-0 flex-1">
                    <DialogTitle className="truncate text-base font-semibold text-label max-sm:text-[0.9375rem]">
                      {detail ? detail.ma_nummer : "Neue Materialanforderung"}
                    </DialogTitle>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      {detail?.linked_aga?.aga_id ? (
                        <a
                          href={`/angebotsanfragen?id=${detail.linked_aga.aga_id}`}
                          className="inline-flex max-w-full items-center truncate rounded-md border border-accent/35 bg-accent/10 px-2 py-0.5 text-xs font-medium text-accent hover:underline"
                          title="Zugehörige Angebotsanfrage öffnen"
                        >
                          AGA · {detail.linked_aga.aga_nummer}
                        </a>
                      ) : null}
                      <p className="text-xs text-label-secondary max-sm:hidden">
                        {detail ? (
                          <>
                            Status:{" "}
                            {maFortschrittLabel(
                              normalizeMonteurFortschritt(
                                detail.monteur_fortschritt,
                                detail.status,
                              ),
                            )}{" "}
                            · erstellt von {detail.created_by} am{" "}
                            {fmtDate(detail.created_at)}
                          </>
                        ) : (
                          "Ein paar Stichworte reichen schon – Foto, Skizze oder Liste anhängen, das Büro erledigt den Rest."
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    {detail?.auftrag_id ? (
                      <ProjectOverviewButton auftragId={detail.auftrag_id} />
                    ) : null}
                    <DialogCloseButton onClick={requestClose} />
                  </div>
                </header>

                <div
                  className={clsx(
                    showBueroView
                      ? "flex min-h-0 flex-1 flex-col overflow-hidden"
                      : "ma-dialog-body min-h-0 flex-1 space-y-4 overflow-y-auto overflow-x-hidden px-5 py-4 material-mobile-scroll max-sm:space-y-3 max-sm:px-3 max-sm:py-2 sm:max-h-[75vh]",
                  )}
                >
                  {loading && !showBueroView ? (
                    <div className="flex items-center justify-center py-8 text-sm text-label-secondary">
                      <Spinner />
                      <span className="ml-2">Lädt …</span>
                    </div>
                  ) : showBueroView && detail ? (
                    <MaBueroPanel
                      detail={detail}
                      onClose={requestClose}
                      onChanged={onChanged}
                      onReload={() => void reload(detail.id, { silent: true })}
                    />
                  ) : (
                    <>
                      {detail ? (
                        <section
                          className="rounded-ios border border-separator bg-card-elevated px-3 py-2.5"
                        >
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-xs text-label-secondary">Stand:</span>
                              <MaFortschrittBadge
                                f={detail.monteur_fortschritt}
                                status={detail.status}
                              />
                            </div>
                            {maBearbeiterDisplayName(detail) ? (
                              <p className="text-xs text-label-secondary">
                                Büro:{" "}
                                <span className="font-medium text-label">
                                  {maBearbeiterDisplayName(detail)}
                                </span>
                              </p>
                            ) : null}
                          </div>
                          {detail.positionen?.length ? (
                            <p className="mt-1.5 text-xs text-label-secondary">
                              {formatMaPositionOrderSummary(detail.positionen) ||
                                `${detail.positionen.length}× Pos. Offen`}
                            </p>
                          ) : null}
                          {!canEdit && detail.bearbeitet_von ? (
                            <p className="mt-2 text-xs text-label-tertiary">
                              Das Büro bearbeitet diese Anforderung – du kannst sie nicht
                              mehr ändern.
                            </p>
                          ) : null}
                        </section>
                      ) : null}

                      <div data-tour="material-auftrag">
                      <LockedAutocomplete<AuftragLookup>
                        className="min-w-0 max-w-full"
                        label="Auftrag"
                        placeholder={
                          isMobile
                            ? "Auftrag suchen …"
                            : "Klicken für zuletzt benutzte – oder Nummer / Kunde / Ort tippen …"
                        }
                        value={auftrag}
                        onChange={(next) => {
                          setAuftrag(next);
                          if (next) setAuftragError(null);
                        }}
                        minChars={0}
                        loadOptions={loadAuftragOptions}
                        disabled={!canEdit}
                        required
                        error={auftragError}
                        emptyText="Noch keine Aufträge benutzt – tippe eine Nummer, einen Kunden oder einen Ort."
                        hint={
                          isMobile
                            ? undefined
                            : "Pflicht – ohne Auftrag kann das Büro die Anforderung nicht zuordnen."
                        }
                      />
                      </div>

                      <div className="space-y-1">
                        <label className="eh-label" htmlFor="ma-liefer-kw">
                          Kalenderwochen
                        </label>
                        <div className="flex min-w-0 items-center gap-2">
                          <input
                            id="ma-liefer-kw"
                            type="text"
                            className="eh-input min-w-0 flex-1"
                            placeholder="z. B. KW32 oder KW32-KW34"
                            value={lieferKwText}
                            disabled={!canEdit}
                            onChange={(e) => applyLieferKwText(e.target.value)}
                          />
                          <MaLieferKwRangePicker
                            value={parsedLieferKw}
                            disabled={!canEdit}
                            onApply={(range) =>
                              applyLieferKwText(
                                formatLieferKw(range, {
                                  asciiHyphen: true,
                                  forceYear: true,
                                }),
                              )
                            }
                          />
                        </div>
                        {lieferKwError ? (
                          <p className="text-xs text-status-red">{lieferKwError}</p>
                        ) : (
                          <p className="text-xs text-label-tertiary">
                            Ungefähres Lieferfenster – per Text oder Wochenkalender, z. B. KW32-KW34/2026
                          </p>
                        )}
                      </div>

                      <div className="ma-liefertermin-row grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-[minmax(0,200px)_minmax(0,1fr)]">
                        <div className="min-w-0 space-y-1">
                          <label className="eh-label" htmlFor="ma-liefertermin">
                            {parsedLieferKw ? "Fixtermin (optional)" : "Liefertermin"}
                          </label>
                          <input
                            id="ma-liefertermin"
                            type="date"
                            className="eh-input ma-liefertermin-input w-full min-w-0 max-w-full"
                            value={liefertermin}
                            min={lieferterminBounds.min}
                            max={lieferterminBounds.max}
                            disabled={!canEdit}
                            onChange={(e) => {
                              const next = e.target.value;
                              setLiefertermin(next);
                              if (!next) {
                                setLieferterminFix(false);
                                setLieferterminError(null);
                                return;
                              }
                              if (parsedLieferKw) {
                                setLieferterminFix(true);
                              }
                              if (
                                isLieferterminBeforeToday(next) &&
                                next !== (detail?.liefertermin ?? "")
                              ) {
                                setLieferterminError(
                                  "Liefertermin muss heute oder in der Zukunft liegen.",
                                );
                              } else if (
                                parsedLieferKw &&
                                !isFixterminWithinLieferKw(next, parsedLieferKw)
                              ) {
                                setLieferterminError(
                                  "Fixtermin muss innerhalb der gewählten Kalenderwochen liegen (spätestens in der letzten KW).",
                                );
                              } else {
                                setLieferterminError(null);
                              }
                            }}
                          />
                          {lieferterminError ? (
                            <p className="text-xs text-status-red">{lieferterminError}</p>
                          ) : parsedLieferKw ? (
                            <p className="text-xs text-label-tertiary">
                              Nur innerhalb der KW, maximal in der letzten gewählten Woche
                            </p>
                          ) : null}
                        </div>
                        <label
                          className={clsx(
                            "flex min-w-0 items-center gap-2 self-end rounded-ios border border-separator bg-card-elevated px-3 py-2 text-sm text-label max-sm:py-1.5",
                            !canEdit && "opacity-60",
                          )}
                        >
                          <input
                            type="checkbox"
                            className="h-4 w-4 shrink-0 rounded border-separator-opaque text-accent focus:ring-accent-soft"
                            checked={lieferterminFix}
                            disabled={!canEdit || !liefertermin}
                            onChange={(e) =>
                              setLieferterminFix(e.target.checked)
                            }
                          />
                          <span className="min-w-0">
                            <span className="font-medium">Fixtermin</span>{" "}
                            <span className="text-xs text-label-secondary max-sm:hidden">
                              – muss verbindlich eingehalten werden
                            </span>
                          </span>
                        </label>
                      </div>

                      <div className="space-y-1">
                        <label className="eh-label" htmlFor="ma-notes">
                          Freitext / Nachricht ans Büro
                        </label>
                        <textarea
                          id="ma-notes"
                          className="eh-input"
                          rows={3}
                          placeholder="Was wird benötigt? Auch grobe Beschreibung ist ok."
                          value={notes}
                          disabled={!canEdit}
                          onChange={(e) => setNotes(e.target.value)}
                        />
                      </div>

                      {isNew && canEdit ? (
                        <div className="space-y-3">
                          <label className="flex min-w-0 items-start gap-2 rounded-ios border border-separator bg-card-elevated px-3 py-2 text-sm text-label">
                            <input
                              type="checkbox"
                              className="mt-0.5 h-4 w-4 shrink-0 rounded border-separator-opaque text-accent focus:ring-accent-soft"
                              checked={alsoOfferAsAga}
                              onChange={(e) => setAlsoOfferAsAga(e.target.checked)}
                            />
                            <span className="min-w-0">
                              <span className="font-medium">
                                Parallel als Angebotsanfrage anbieten
                              </span>
                              <span className="mt-0.5 block text-xs text-label-secondary">
                                Erstellt zeitgleich eine Angebotsanfrage mit denselben
                                Positionen, dem Freitext und den Anhängen.
                              </span>
                            </span>
                          </label>

                          {alsoOfferAsAga ? (
                            <div className="space-y-3 rounded-ios border border-separator bg-fill-tertiary/40 p-3">
                              <div className="eh-label">Ansprechpartner</div>
                              <div className="flex flex-wrap gap-2">
                                <label
                                  className={clsx(
                                    "inline-flex items-center gap-2 rounded-ios border px-3 py-2 text-sm",
                                    agaContactMode === "project"
                                      ? "border-accent bg-accent-soft text-accent"
                                      : "border-separator-opaque text-label",
                                    !agaProjectHasContact && "opacity-50",
                                    agaProjectHasContact && "cursor-pointer",
                                  )}
                                >
                                  <input
                                    type="radio"
                                    name="ma-aga-contact-mode"
                                    className="sr-only"
                                    checked={agaContactMode === "project"}
                                    disabled={!agaProjectHasContact}
                                    onChange={() => setAgaContactMode("project")}
                                  />
                                  Aus Projekt übernehmen
                                </label>
                                <label
                                  className={clsx(
                                    "inline-flex cursor-pointer items-center gap-2 rounded-ios border px-3 py-2 text-sm",
                                    agaContactMode === "new"
                                      ? "border-accent bg-accent-soft text-accent"
                                      : "border-separator-opaque text-label",
                                  )}
                                >
                                  <input
                                    type="radio"
                                    name="ma-aga-contact-mode"
                                    className="sr-only"
                                    checked={agaContactMode === "new"}
                                    onChange={() => setAgaContactMode("new")}
                                  />
                                  Neuer Ansprechpartner
                                </label>
                              </div>

                              {agaContactMode === "project" ? (
                                <div className="rounded-ios border border-separator-opaque bg-card p-3 text-sm">
                                  {agaProjectHasContact ? (
                                    <>
                                      <div className="font-medium text-label">
                                        {agaProjectContact?.name || "Ansprechpartner"}
                                      </div>
                                      <div className="text-label-secondary">
                                        {[
                                          agaProjectContact?.company,
                                          agaProjectContact?.phone,
                                          agaProjectContact?.email,
                                        ]
                                          .filter(Boolean)
                                          .join(" · ") || "Keine weiteren Angaben"}
                                      </div>
                                    </>
                                  ) : (
                                    <div className="text-label-secondary">
                                      {selectedAuftragId
                                        ? "Für dieses Projekt ist kein Ansprechpartner hinterlegt – bitte einen neuen anlegen."
                                        : "Bitte zuerst einen Auftrag auswählen."}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                  <div className="space-y-1">
                                    <label className="eh-label" htmlFor="ma-aga-contact-name">
                                      Name<span className="ml-1 text-status-red">*</span>
                                    </label>
                                    <input
                                      id="ma-aga-contact-name"
                                      className="eh-input"
                                      value={agaContactName}
                                      onChange={(e) => setAgaContactName(e.target.value)}
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <label
                                      className="eh-label"
                                      htmlFor="ma-aga-contact-company"
                                    >
                                      Firma
                                    </label>
                                    <input
                                      id="ma-aga-contact-company"
                                      className="eh-input"
                                      value={agaContactCompany}
                                      onChange={(e) => setAgaContactCompany(e.target.value)}
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <label className="eh-label" htmlFor="ma-aga-contact-phone">
                                      Telefon
                                    </label>
                                    <input
                                      id="ma-aga-contact-phone"
                                      className="eh-input"
                                      value={agaContactPhone}
                                      onChange={(e) => setAgaContactPhone(e.target.value)}
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <label className="eh-label" htmlFor="ma-aga-contact-email">
                                      E-Mail
                                    </label>
                                    <input
                                      id="ma-aga-contact-email"
                                      type="email"
                                      className="eh-input"
                                      value={agaContactEmail}
                                      onChange={(e) => setAgaContactEmail(e.target.value)}
                                    />
                                  </div>
                                </div>
                              )}

                              <div className="space-y-1">
                                <label className="eh-label" htmlFor="ma-aga-contact-note">
                                  Notiz zum Ansprechpartner
                                </label>
                                <textarea
                                  id="ma-aga-contact-note"
                                  className="eh-input min-h-[2.5rem]"
                                  rows={2}
                                  value={agaContactNote}
                                  onChange={(e) => setAgaContactNote(e.target.value)}
                                />
                              </div>
                            </div>
                          ) : null}
                        </div>
                      ) : null}

                      {/* Quick Actions */}
                      <div className="ma-quick-actions flex flex-wrap gap-2 max-sm:gap-1.5">
                        <Button
                          variant="secondary"
                          size={isMobile ? "sm" : "md"}
                          data-tour="material-position"
                          onClick={() => addEmptyPosition()}
                          disabled={!canEdit}
                        >
                          + Position
                        </Button>
                        <Button
                          variant="secondary"
                          size={isMobile ? "sm" : "md"}
                          onClick={() => setArtikelScanOpen(true)}
                          disabled={!canEdit}
                          leftIcon={<QrCodeIcon className="h-4 w-4" />}
                        >
                          QR scannen
                        </Button>
                        <Button
                          variant="secondary"
                          size={isMobile ? "sm" : "md"}
                          onClick={() => fileInputRef.current?.click()}
                          disabled={!canEdit}
                        >
                          {isMobile ? "+ Bild/PDF" : "Bild / PDF anhängen"}
                        </Button>
                        {!isMobile && canEdit ? (
                          <Button
                            variant="secondary"
                            size="md"
                            onClick={() => void openMobileUpload()}
                            disabled={mobileUploadPreparing}
                          >
                            {mobileUploadPreparing ? "Speichern …" : "Vom Handy hochladen"}
                          </Button>
                        ) : null}
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*,application/pdf"
                          multiple
                          hidden
                          onChange={(e) => {
                            void handleFiles(e.target.files);
                            e.target.value = "";
                          }}
                        />
                      </div>

                      <p className="text-xs text-label-tertiary max-sm:hidden">
                        Tipp: Bilder und PDFs kannst du auch in dieses Fenster ziehen.
                        Artikel-Etikett per „QR scannen“ erfassen – nach der Menge
                        landet die Position in der Liste. Die KI füllt Positionen aus Fotos.
                      </p>

                      {/* KI-Vorschläge */}
                      {(aiLoading || aiResult || aiError) && (
                        <AiSuggestionsPanel
                          loading={aiLoading}
                          result={aiResult}
                          error={aiError}
                          fileName={aiFileName}
                          auftragHintStatus={auftragHintStatus}
                          onApply={applyAiSuggestions}
                          onDismiss={dismissAi}
                        />
                      )}

                      {/* Positionen */}
                      <PositionList
                        positions={positions}
                        canEdit={canEdit}
                        onPatch={patchPosition}
                        onRemove={removePosition}
                        onPendingText={setPendingPosText}
                      />

                      {/* Anhänge */}
                      <Attachments
                        detail={detail}
                        pending={pending}
                        onRemoveStored={removeStoredAttachment}
                        onRemovePending={removePendingAttachment}
                        onPreview={(source) => {
                          setLightboxUrl(source.url);
                          setLightboxCopySource(source);
                        }}
                        onAnalyze={(file) => void runAiAnalysis(file)}
                      />

                      {/* Verknüpfungen */}
                      {detail &&
                      (detail.linked_anfragen.length ||
                        detail.linked_orders.length ||
                        detail.linked_aga) ? (
                        <section className="rounded-ios border border-separator bg-card-elevated p-3 text-sm">
                          <h3 className="mb-1 font-semibold text-label">
                            Verknüpft
                          </h3>
                          <ul className="space-y-1">
                            {detail.linked_aga ? (
                              <li>
                                AGA:{" "}
                                <a
                                  href={`/angebotsanfragen?id=${detail.linked_aga.aga_id}`}
                                  className="text-accent hover:underline"
                                >
                                  {detail.linked_aga.aga_nummer}
                                </a>
                              </li>
                            ) : null}
                            {detail.linked_anfragen.map((l) => (
                              <li key={`an-${l.anfrage_id}`}>
                                TA:{" "}
                                <a
                                  href={`/anfragen?id=${l.anfrage_id}`}
                                  className="text-accent hover:underline"
                                >
                                  {l.ta_subject || `#${l.anfrage_id}`}
                                </a>{" "}
                                · {l.supplier_name || ""}
                              </li>
                            ))}
                            {detail.linked_orders.map((l) => (
                              <li key={`ord-${l.order_id}`}>
                                TK:{" "}
                                <a
                                  href={`/bestellungen?id=${l.order_id}`}
                                  className={
                                    l.cancelled_at
                                      ? "eh-order-tk-badge eh-order-tk-badge--cancelled font-mono text-xs font-semibold hover:underline"
                                      : "text-accent hover:underline"
                                  }
                                >
                                  {l.bestellnummer || l.subject || `#${l.order_id}`}
                                </a>
                                {l.supplier_name ? ` · ${l.supplier_name}` : ""}
                                {l.cancelled_at ? (
                                  <span className="text-status-red"> · storniert</span>
                                ) : null}
                              </li>
                            ))}
                          </ul>
                        </section>
                      ) : null}
                    </>
                  )}
                </div>

                {!showBueroView ? (
                <footer
                  className="ma-dialog-footer ma-dialog-footer--compact flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-separator bg-card-elevated px-5 py-3 max-sm:flex-nowrap max-sm:gap-1.5 max-sm:px-3 max-sm:py-2 max-sm:pb-[max(0.5rem,env(safe-area-inset-bottom))]"
                >
                  <div className="mr-auto text-xs text-label-tertiary max-sm:hidden">
                    {detail ? (
                      <>
                        Zuletzt aktualisiert: {fmtDate(detail.updated_at)}
                      </>
                    ) : null}
                  </div>
                  {detail && detail.status === "offen" && canEdit ? (
                    <Button
                      variant="ghost"
                      size={isMobile ? "sm" : "md"}
                      onClick={handleDelete}
                      className="max-sm:hidden"
                    >
                      Löschen
                    </Button>
                  ) : null}
                  <div className="flex items-center gap-2 max-sm:min-w-0 max-sm:flex-1">
                    <Button
                      variant="primary"
                      size={isMobile ? "sm" : "md"}
                      data-tour="material-speichern"
                      onClick={handleSave}
                      loading={saving}
                      disabled={!canEdit && !hasUnsaved}
                      className="max-sm:flex-1"
                    >
                      Speichern
                    </Button>
                    <Button
                      variant="ghost"
                      size={isMobile ? "sm" : "md"}
                      onClick={requestClose}
                      className="max-sm:shrink-0"
                    >
                      Abbrechen
                    </Button>
                  </div>
                </footer>
                ) : null}
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>

        <Lightbox
          url={lightboxUrl}
          copySource={lightboxCopySource}
          onClose={() => {
            setLightboxUrl(null);
            setLightboxCopySource(null);
          }}
        />
      </HuiDialog>
    </Transition>

    <MaArtikelScanDialog
      open={artikelScanOpen}
      onClose={() => setArtikelScanOpen(false)}
      onAdd={addScannedArtikel}
    />

    <MaMobileUploadDialog
      open={mobileUploadOpen}
      onClose={() => setMobileUploadOpen(false)}
      maId={detail?.id ?? maId ?? createdMaIdRef.current ?? null}
      onUploaded={() => {
        const id = detail?.id ?? maId ?? createdMaIdRef.current ?? null;
        if (id) void reload(id, { silent: true });
      }}
    />

    <DiscardChangesDialog
      open={confirmOpen}
      onConfirm={confirmDiscard}
      onCancel={cancelDiscard}
      zIndexClass={overlayZIndexClass}
    />
    </>
  );
}

/* ─────────────────────────── Hilfs-Komponenten ─────────────────────────── */

interface PositionListProps {
  positions: PositionDraft[];
  canEdit: boolean;
  onPatch: (localId: string, patch: Partial<PositionDraft>) => void;
  onRemove: (localId: string) => void;
  onPendingText: (localId: string, text: string) => void;
}

function PositionList({
  positions,
  canEdit,
  onPatch,
  onRemove,
  onPendingText,
}: PositionListProps) {
  if (positions.length === 0) {
    return (
      <p className="rounded-ios border border-dashed border-separator-opaque p-3 text-center text-xs text-label-tertiary">
        Noch keine Positionen. Scanne ein Artikel-Etikett, klicke auf
        „+ Position“, lade ein Bild hoch (KI füllt aus) oder beschreibe
        deinen Bedarf im Freitext oben.
      </p>
    );
  }
  const asMaPositions = positions.map((p) => ({
    id: p.id ?? 0,
    sort_index: null,
    menge: null,
    einheit: null,
    text: p.text,
    link_url: null,
    notes: null,
    artikel_id: null,
    hero_nr: null,
    hero_unit: null,
    ean: null,
    bereitgelegt_at: p.bereitgelegt_at ?? null,
    position_status: p.position_status ?? null,
    position_status_grund: p.position_status_grund ?? null,
    ordered_links: p.ordered_links,
    draft_links: p.draft_links,
  }));
  const orderSummary = maPositionOrderSummary(asMaPositions);
  const orderSummaryText = formatMaPositionOrderSummary(asMaPositions);
  const showOrderStatus =
    orderSummary.geliefert > 0 ||
    orderSummary.ordered > 0 ||
    orderSummary.bereitgelegt > 0 ||
    orderSummary.draftOnly > 0 ||
    orderSummary.storniert > 0 ||
    orderSummary.abgelehnt > 0;
  return (
    <div className="space-y-2">
      {showOrderStatus && orderSummaryText ? (
        <p className="text-xs text-label-secondary">{orderSummaryText}</p>
      ) : null}
    <ul className="space-y-2">
      {positions.map((p, idx) => (
        <li
          key={p.localId}
          data-pos-local={p.localId}
          className="min-w-0 max-w-full rounded-ios border border-separator bg-card p-3 shadow-ios max-sm:p-2"
        >
          <div className="flex items-center justify-between gap-2">
            <span className="flex min-w-0 flex-wrap items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-label-secondary">
              <span>Pos. {idx + 1}</span>
              {p.from_ai ? (
                <span
                  className="rounded-full bg-status-orange/15 px-2 py-0.5 text-[11px] font-medium normal-case tracking-normal text-status-orange"
                  title="Diese Position wurde von der KI erkannt und sollte noch geprüft werden."
                >
                  Von KI erkannt
                </span>
              ) : null}
              {p.hero_nr ? (
                <span className="rounded-full bg-accent-soft px-2 py-0.5 text-[11px] font-normal normal-case tracking-normal text-accent">
                  Hero {p.hero_nr}
                </span>
              ) : null}
              {showOrderStatus && p.id != null ? (
                <MaPositionOrderBadge
                  position={{
                    id: p.id,
                    sort_index: null,
                    menge: null,
                    einheit: null,
                    text: p.text,
                    link_url: null,
                    notes: null,
                    artikel_id: null,
                    hero_nr: null,
                    hero_unit: null,
                    ean: null,
                    bereitgelegt_at: p.bereitgelegt_at ?? null,
                    position_status: p.position_status ?? null,
                    position_status_grund: p.position_status_grund ?? null,
                    ordered_links: p.ordered_links,
                    draft_links: p.draft_links,
                  }}
                />
              ) : null}
            </span>
            {canEdit ? (
              <button
                type="button"
                className="text-xs text-status-red hover:underline"
                onClick={() => onRemove(p.localId)}
              >
                Entfernen
              </button>
            ) : null}
          </div>
          <div className="mt-2 grid min-w-0 grid-cols-1 gap-2 sm:grid-cols-[80px_120px_1fr]">
            <input
              type="text"
              inputMode="decimal"
              className="eh-input"
              placeholder="Menge"
              value={p.menge}
              data-field="menge"
              disabled={!canEdit}
              onChange={(e) => onPatch(p.localId, { menge: e.target.value })}
            />
            <MaPositionEinheitField
              value={p.einheit}
              disabled={!canEdit}
              onChange={(einheit) => onPatch(p.localId, { einheit })}
            />
            <MaPositionMaterialField
              value={{
                text: p.text,
                einheit: p.einheit,
                artikel_id: p.artikel_id,
                hero_nr: p.hero_nr,
                hero_unit: p.hero_unit,
                ean: p.ean,
              }}
              disabled={!canEdit}
              onChange={(patch) => onPatch(p.localId, patch)}
              onPendingTextChange={(text) => onPendingText(p.localId, text)}
            />
          </div>
          {(p.notes || canEdit) && (
            <textarea
              className="eh-input mt-2"
              rows={1}
              placeholder="Notiz (optional)"
              value={p.notes}
              data-field="notes"
              disabled={!canEdit}
              onChange={(e) => onPatch(p.localId, { notes: e.target.value })}
            />
          )}
        </li>
      ))}
    </ul>
    </div>
  );
}

function MaPositionEinheitField({
  value,
  disabled,
  onChange,
}: {
  value: string;
  disabled?: boolean;
  onChange: (einheit: string) => void;
}) {
  const unitValue = useMemo((): AutocompleteOption<string> | null => {
    const normalized = normalizeMaterialUnit(value);
    if (!normalized) return null;
    return { key: normalized, label: normalized, value: normalized };
  }, [value]);

  return (
    <div className="[&_.eh-input]:text-sm">
      <LockedAutocomplete<string>
        placeholder="Einheit"
        value={unitValue}
        disabled={disabled}
        minChars={0}
        loadOptions={async (q) =>
          filterMaterialUnits(q).map((u) => ({ key: u, label: u, value: u }))
        }
        onChange={(opt) => onChange(opt?.value ?? "")}
      />
    </div>
  );
}

interface AttachmentsProps {
  detail: MaDetail | null;
  pending: PendingAttachment[];
  onRemoveStored: (attachmentId: number) => void;
  onRemovePending: (localId: string) => void;
  onPreview: (source: { url: string; filename: string; contentType: string }) => void;
  onAnalyze: (file: File) => void;
}

function Attachments({
  detail,
  pending,
  onRemoveStored,
  onRemovePending,
  onPreview,
  onAnalyze,
}: AttachmentsProps) {
  const { openMediaContextMenu, contextMenuNode } = useMaMediaContextMenu();
  const stored = (detail?.attachments || []).filter((a) => a.position_id == null);
  if (stored.length === 0 && pending.length === 0) return null;
  return (
    <section>
      <h3 className="mb-2 text-sm font-semibold text-label">Anhänge</h3>
      <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
        {stored.map((a) => {
          const isImg = (a.content_type || "").startsWith("image/");
          const fullUrl = detail ? getMaAttachmentUrl(detail.id, a.id) : "";
          const thumbUrl = detail ? getMaAttachmentThumbUrl(detail.id, a.id) : "";
          return (
            <li
              key={`a-${a.id}`}
              className="group relative overflow-hidden rounded-ios border border-separator bg-card"
            >
              {isImg ? (
                <button
                  type="button"
                  onClick={() =>
                    onPreview({
                      url: thumbUrl,
                      filename: a.filename,
                      contentType: a.content_type,
                    })
                  }
                  onContextMenu={(e) =>
                    openMediaContextMenu(e, {
                      url: fullUrl,
                      filename: a.filename,
                      contentType: a.content_type,
                      isImage: true,
                      imageElement: imageElementFromContextEvent(e.target, e.currentTarget),
                    })
                  }
                  className="block h-32 w-full overflow-hidden bg-fill-tertiary"
                >
                  <img
                    src={thumbUrl}
                    alt={a.filename}
                    loading="lazy"
                    crossOrigin="use-credentials"
                    className="h-full w-full object-cover transition group-hover:scale-105"
                  />
                </button>
              ) : (
                <a
                  href={fullUrl}
                  target="_blank"
                  rel="noopener"
                  onContextMenu={(e) =>
                    openMediaContextMenu(e, {
                      url: fullUrl,
                      filename: a.filename,
                      contentType: a.content_type,
                      isImage: false,
                    })
                  }
                  className="block px-3 py-6 text-center text-sm text-label hover:bg-fill-tertiary"
                >
                  {a.filename}
                </a>
              )}
              <button
                type="button"
                aria-label="Entfernen"
                className="ma-att-remove absolute right-1 top-1 inline-flex h-7 w-7 items-center justify-center rounded-full bg-card/90 text-xs text-status-red opacity-0 shadow-ios transition group-hover:opacity-100 max-sm:opacity-100"
                onClick={() => onRemoveStored(a.id)}
              >
                ✕
              </button>
            </li>
          );
        })}
        {pending.map((p) => {
          const isImg = p.file.type.startsWith("image/");
          return (
            <li
              key={p.localId}
              className="group relative overflow-hidden rounded-ios border border-dashed border-accent/50 bg-card"
            >
              {isImg ? (
                <button
                  type="button"
                  onClick={() =>
                    onPreview({
                      url: p.previewUrl,
                      filename: p.file.name,
                      contentType: p.file.type,
                    })
                  }
                  onContextMenu={(e) =>
                    openMediaContextMenu(e, {
                      file: p.file,
                      filename: p.file.name,
                      contentType: p.file.type,
                      isImage: true,
                      imageElement: imageElementFromContextEvent(e.target, e.currentTarget),
                    })
                  }
                  className="block h-32 w-full overflow-hidden bg-fill-tertiary"
                >
                  <img
                    src={p.previewUrl}
                    alt={p.file.name}
                    className="h-full w-full object-cover"
                  />
                </button>
              ) : (
                <span
                  onContextMenu={(e) =>
                    openMediaContextMenu(e, {
                      file: p.file,
                      filename: p.file.name,
                      contentType: p.file.type,
                      isImage: false,
                    })
                  }
                  className="block cursor-default px-3 py-6 text-center text-sm text-label"
                >
                  {p.file.name}
                </span>
              )}
              <div className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-card/90 px-2 py-1 text-[11px]">
                <span className="text-label-secondary">
                  Wird beim Speichern hochgeladen
                </span>
                {isImg && (
                  <button
                    type="button"
                    className="text-accent hover:underline"
                    onClick={() => onAnalyze(p.file)}
                  >
                    KI-Analyse
                  </button>
                )}
              </div>
              <button
                type="button"
                aria-label="Entfernen"
                className="ma-att-remove absolute right-1 top-1 inline-flex h-7 w-7 items-center justify-center rounded-full bg-card/90 text-xs text-status-red opacity-0 shadow-ios transition group-hover:opacity-100 max-sm:opacity-100"
                onClick={() => onRemovePending(p.localId)}
              >
                ✕
              </button>
            </li>
          );
        })}
      </ul>
      {contextMenuNode}
    </section>
  );
}

/* ─────────────────────────── Hilfsfunktionen außen ─────────────────────────── */

function auftragToOption(
  row: AuftragLookup,
): AutocompleteOption<AuftragLookup> {
  return {
    key: String(row.id),
    label: formatAuftragNumberWithYear(row),
    description: [row.kunde, row.ort_zusatz || row.ort, row.beschreibung]
      .filter((v): v is string => !!v)
      .join(" · "),
    value: row,
  };
}

async function loadAuftragOptions(
  query: string,
): Promise<AutocompleteOption<AuftragLookup>[]> {
  const trimmed = query.trim();
  // Klick ins leere Feld → die zuletzt vom User benutzten Aufträge anzeigen
  // (analog `bestellungen.js` der klassischen App).  Bei normaler Eingabe
  // greift wieder die Volltextsuche.
  if (!trimmed) {
    const list = await getRecentAuftraege({ limit: 25 }).catch(
      () => [] as AuftragLookup[],
    );
    return list.map(auftragToOption);
  }
  const list = await searchAuftraege(trimmed).catch(() => [] as AuftragLookup[]);
  return list.map(auftragToOption);
}
